<template>
  <template v-if="page">
    <div
      :style="`background-image : url(${cdnImage(page.heroImage?.asset?._ref).bg}); min-height: 150px; background-size: cover; background-repeat: no-repeat; background-position: bottom right;`"
      :class="{
        'flex items-center justify-center bg-www-color-accent-dark py-100px': true,
      }"
      data-test="resourceCenterHero"
    >
      <h1
        v-sc-non-empty-string="page.title"
        :class="{
          'text-center text-28px font-extrabold leading-8 text-www-color-white lg:text-6xl': true,
        }"
      >
        {{ page.title }}
      </h1>
    </div>
    <div
      :class="{
        'fluid-container': true,
      }"
    >
      <div
        :class="{
          'grid grid-cols-12 pb-24 pt-14': true,
        }"
      >
        <div
          :class="{
            'col-span-12 px-3 lg:col-span-3': true,
          }"
        >
          <div
            :class="{
              'block lg:hidden': true,
            }"
            data-testid="MobileFilterButton"
          >
            <Modal
              :pill-bar-pills="pillBarPills"
              :clear-all="page.clearAll"
              :filter-topics-by="page.filterTopicsBy"
              :show-results="page.showResults"
              @taxonomy-change="onTaxonomyChanged"
            >
              <FilterOptionsSideBar
                :taxonomy-options-config="taxonomyOptionsConfig"
                :taxonomy-checked-states="taxonomyCheckedStates"
                :loaded="blogData.total > 0"
                data-testid="filterOptionsMobile"
                @taxonomy-change="onTaxonomyChanged"
              />
            </Modal>
          </div>
          <FilterOptionsSideBar
            :taxonomy-options-config="taxonomyOptionsConfig"
            :taxonomy-checked-states="taxonomyCheckedStates"
            :loaded="blogData.total > 0"
            :class="{
              'hidden lg:block': true,
            }"
            data-testid="filterOptionsDesktop"
            @taxonomy-change="onTaxonomyChanged"
          />
        </div>
        <div
          :class="{
            'col-span-12 px-3 lg:col-span-9': true,
          }"
        >
          <div>
            <ResourceCenterLoader v-if="isLoading" />

            <div v-if="isFilterEventsQueued">
              <p>Filtering...</p>
            </div>

            <FilterOptionsPillBar
              :pill-bar-pills="pillBarPills"
              :result-count="blogData.total"
              @taxonomy-change="onTaxonomyChanged"
            />

            <FilterOptionsCardsContainer
              :class="{
                'md:grid-cols-3': true,
              }"
              :card-data="blogData.items"
              :is-loading="isLoading"
              data-testid="FilterCardsContainer"
            >
              <template #cards>
                <CardBlog
                  v-if="page.featuredAsset && pageNumber == 1 && !pillBarPills.length"
                  :blog="page.featuredAsset.reference"
                  :is-featured="true"
                  @taxonomy-change="onTaxonomyChanged"
                />
                <CardBlog
                  v-for="blog in blogData.items"
                  :key="blog._id"
                  :blog="blog"
                  @taxonomy-change="onTaxonomyChanged"
                />
              </template>
            </FilterOptionsCardsContainer>

            <FilterOptionsPaginationBar
              :total="blogData.total"
              :page-number="pageNumber"
              :page-limit="pageLimit"
              :go-to-page="page.goToPage"
              data-testid="FilterPaginationBar"
              @page-change="onPageChange"
            />
          </div>
        </div>
      </div>
    </div>
  </template>
</template>

<script setup lang="ts">
import Modal from '~/components/Atmos/Modal/Modal.vue';
import ResourceCenterLoader from '~/components/Atmos/ResourceCenterLoader/ResourceCenterLoader.vue';
import CardBlog from '~/components/Molecules/CardBlog/CardBlog.vue';
import FilterOptionsCardsContainer from '~/components/Molecules/ContentFilter/FilterOptionsCardsContainer.vue';
import FilterOptionsPaginationBar from '~/components/Molecules/ContentFilter/FilterOptionsPaginationBar.vue';
import FilterOptionsPillBar from '~/components/Molecules/ContentFilter/FilterOptionsPillBar.vue';
import FilterOptionsSideBar from '~/components/Molecules/ContentFilter/FilterOptionsSideBar.vue';
import { allTaxonomyConcepts } from '~/queries/components/Atoms/AllTaxonomyConcepts.query';
import { pageResourceCenterIndex } from '~/queries/pages/PageResourceCenterIndex.query';
import type { AllTaxonomyConceptsResult, PageResourceCenterIndexResult } from '~/sanity/types';
interface PropsType {
  contentType: Array<SupportedContentType> | SupportedContentType;
  sortDirection?: 'asc' | 'desc';
}
const props = defineProps<PropsType>();
const { path: currentPathname } = useRoute();

const { data: page } = await useWwwSanityQueryNonNull<PageResourceCenterIndexResult>(
  pageResourceCenterIndex,
  {
    currentPathname,
  }
);

const { data: allConcepts } = await useWwwSanityQueryNonNull<AllTaxonomyConceptsResult>(
  allTaxonomyConcepts,
  {
    slug: 'taxonomies',
  }
);

if (!allConcepts.value) {
  throw new Error('No data found for all taxonomies concepts');
}
if (!page.value || !page.value.filters) {
  throw new Error('No data found for page filters');
}

if (page.value.heroImage?.asset?._ref) {
  preloadImgSrc(cdnImage(page.value.heroImage.asset._ref).bg);
}

const {
  isLoading,
  pageLimit,
  pageNumber,
  paginationResult: blogData,
  taxonomyOptionsConfig,
  taxonomyCheckedStates,
  isFilterEventsQueued,
  pillBarPills,
  onPageChange,
  onTaxonomyChanged,
} = await useContentFilter({
  contentType: props.contentType,
  allTaxonomyConcepts: allConcepts.value,
  topLevelFilters: page.value.filters,
  sortDirection: props.sortDirection || 'desc',
});
usePageHead({
  _id: page.value?._id,
  description: page.value.ogDescription || page.value?.description,
  ogImage:
    page.value.ogImage?.asset || page.value?.cardImage?.asset || page.value?.heroImage?.asset,
  excludeFromExternalSearch: page.value?.excludeFromExternalSearch,
  title: page.value?.htmlTitle || page.value?.title,
  twitterCard: 'summary',
});
</script>

<style lang="scss" scoped>
@-webkit-keyframes lds-ring {
  0% {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(1turn);
  }
}

@keyframes lds-ring {
  0% {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(1turn);
  }
}
.loader {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: hsla(0, 0%, 100%, 0.4);
  min-height: 160px;
  z-index: 1;
  .lds-ring {
    display: inline-block;
    position: relative;
    width: 80px;
    height: 80px;
    div {
      box-sizing: border-box;
      display: block;
      position: absolute;
      width: 64px;
      height: 64px;
      margin: 8px;
      border-radius: 50%;
      -webkit-animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
      animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
      border: 8px solid transparent;
      border-top-color: #81bb5b;
      &:first-child {
        -webkit-animation-delay: -0.45s;
        animation-delay: -0.45s;
      }
      &:nth-child(2) {
        -webkit-animation-delay: -0.3s;
        animation-delay: -0.3s;
      }
      &:nth-child(3) {
        -webkit-animation-delay: -0.15s;
        animation-delay: -0.15s;
      }
    }
  }
}
</style>
